import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  ConfigProvider,
  Divider,
  Input,
  Select,
  Space,
  Typography,
} from "antd";
import { useReviSceneController } from "./useReviSceneController";
import {
  ReviSceneList,
  ReviSceneWizardModal,
} from "../../../components/Admin/ReviSceneCmp";
import "./Page_ReviScene.scss";

const { Title, Text } = Typography;

export function Page_ReviScene() {
  const { state, actions } = useReviSceneController();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { sceneId, initialValues, bannerFileList, existingBannerId }
  const [loadingEdit, setLoadingEdit] = useState(false);

  const therapyOptions = useMemo(
    () =>
      (state.therapyCats || []).map((t) => ({
        label: t.title,
        value: String(t._id),
      })),
    [state.therapyCats],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setWizardOpen(true);
  }, []);

  const openEdit = useCallback(
    async (scene) => {
      const sceneId = scene?._id;
      if (!sceneId) return;

      setWizardOpen(true);
      setLoadingEdit(true);
      setEditing(null);

      try {
        const data = await actions.getEditData(sceneId);
        setEditing(data);
      } catch (e) {
        // Si falla, cerramos para no dejar un modal roto
        setWizardOpen(false);
      } finally {
        setLoadingEdit(false);
      }
    },
    [actions],
  );

  const closeWizard = useCallback(() => {
    if (state.creating) return;
    setWizardOpen(false);
    setEditing(null);
    setLoadingEdit(false);
  }, [state.creating]);

  const handleSubmit = useCallback(
    async (payload) => {
      if (editing?.sceneId) {
        await actions.updateScene(editing.sceneId, payload);
      } else {
        await actions.createScene(payload);
      }
    },
    [actions, editing],
  );

  return (
    <AntApp>
      <ConfigProvider theme={{}}>
        <div className="page-revi-scene">
          <div className="page-revi-scene__header">
            <div>
              <Title level={3} className="page-revi-scene__title">
                Salas REVI
              </Title>
              <Text type="secondary">
                Gestión de salas clínicas (VirtualScene + Module 07).
              </Text>
            </div>

            <Space wrap>
              <Button onClick={actions.reload} loading={state.loadingScenes}>
                Recargar
              </Button>
              <Button type="primary" onClick={openCreate}>
                Nueva sala
              </Button>
            </Space>
          </div>

          {state.error && (
            <Alert
              className="page-revi-scene__alert"
              type="error"
              showIcon
              message="Error"
              description={state.error.message}
              closable
            />
          )}

          <Card className="page-revi-scene__card">
            <div className="page-revi-scene__filters">
              <Input.Search
                allowClear
                placeholder="Buscar por título…"
                onSearch={(v) => actions.setFilterPatch({ search: v })}
                className="page-revi-scene__filter"
              />

              <Select
                allowClear
                placeholder="Filtrar por Terapia"
                value={state.filters.therapyId || undefined}
                onChange={(v) =>
                  actions.setFilterPatch({ therapyId: v || null })
                }
                options={therapyOptions}
                loading={state.loadingCats}
                showSearch
                optionFilterProp="label"
                className="page-revi-scene__filter"
              />

              <Select
                allowClear
                placeholder="Dificultad"
                value={state.filters.difficulty || undefined}
                onChange={(v) =>
                  actions.setFilterPatch({ difficulty: v || null })
                }
                options={[
                  { label: "Preventivo", value: "Preventivo" },
                  { label: "Leve", value: "Leve" },
                  { label: "Moderado", value: "Moderado" },
                  { label: "Severo", value: "Severo" },
                  { label: "Todos", value: "Todos" },
                ]}
                className="page-revi-scene__filter page-revi-scene__filter--sm"
              />

              <Select
                allowClear
                placeholder="Premium"
                value={state.filters.premium ?? undefined}
                onChange={(v) => actions.setFilterPatch({ premium: v ?? null })}
                options={[
                  { label: "Sí (Pro)", value: true },
                  { label: "No (Free)", value: false },
                ]}
                className="page-revi-scene__filter page-revi-scene__filter--sm"
              />

              <Select
                allowClear
                placeholder="Activo"
                value={state.filters.active ?? undefined}
                onChange={(v) => actions.setFilterPatch({ active: v ?? null })}
                options={[
                  { label: "Sí", value: true },
                  { label: "No", value: false },
                ]}
                className="page-revi-scene__filter page-revi-scene__filter--sm"
              />
            </div>

            <Divider className="page-revi-scene__divider" />

            <ReviSceneList
              scenes={state.scenes}
              therapyCats={state.therapyCats}
              loading={state.loadingScenes}
              q={state.filters}
              total={state.total}
              onPageChange={actions.setPage}
              onDelete={actions.deleteScene}
              onEdit={openEdit}
            />
          </Card>

          <ReviSceneWizardModal
            open={wizardOpen}
            onClose={closeWizard}
            therapyCats={state.therapyCats}
            onSubmit={handleSubmit}
            creating={state.creating}
            mode={editing?.sceneId ? "edit" : "create"}
            initialData={editing}
            loadingInitial={loadingEdit}
          />
        </div>
      </ConfigProvider>
    </AntApp>
  );
}
